<template>
    <div :class="cardClass">
        <template v-if="!isLoading">
            <div class="RPBulletGraph__header-bar">
                <div class="RPBulletGraph__header">
                    <slot name="header">
                        <span class="RPBulletGraph__header-text">{{ header }}</span>
                        <RPTooltip
                            v-if="headerTooltip !== ''"
                            class="RPBulletGraph__header-tooltip"
                            :tooltip="headerTooltip"
                        />
                    </slot>
                </div>
                <div class="RPBulletGraph__title-action">
                    <slot name="action"></slot>
                </div>
            </div>
            <div class="RPBulletGraph__chart-container">
                <template v-if="type === 'multi'">
                    <BulletGraph
                        class="RPBulletGraph__chart"
                        :type="type"
                        :chart-data="chartDataComputed"
                    />
                </template>
                <template v-else>
                    <BulletGraph
                        v-for="chart in chartDataComputed"
                        :key="chart.label"
                        class="RPBulletGraph__chart"
                        :type="type"
                        :chart-data="chart"
                    >
                        <template v-slot:header="{data}">
                            <slot
                                name="chartHeader"
                                :data="data"
                            ></slot>
                        </template>
                        <template v-slot:info="{data}">
                            <slot
                                name="info"
                                :data="data"
                            ></slot>
                        </template>
                        <template v-slot:percent="{data}">
                            <slot
                                name="percent"
                                :data="data"
                            ></slot>
                        </template>
                    </BulletGraph>
                </template>
            </div>
            <div
                v-if="showLegend"
                class="RPBulletGraph__legend-container"
            >
                <LegendBar :legend="legend" />
            </div>
        </template>
    </div>
</template>
<script>
import RPTooltip from 'components/Basic/Misc/RPTooltip.vue';
import BulletGraph from './_bullet-graph.vue';
import stat from 'status';
import statusBuilder from 'status/statusBuilder';
import getRandomColor from '../getRandomColor';
import has from 'lodash/has';
import LegendBar from './_legend-bar.vue';

export default {
    name: 'RPBulletGraph',
    components: {
        BulletGraph,
        RPTooltip,
        LegendBar
    },
    props: {
        header: {
            type: String,
            required: true
        },
        headerTooltip: {
            type: String,
            default: ''
        },
        chartData: {
            type: Array,
            required: true,
        },
        type: {
            type: String,
            default: 'thin',
            validator: val => ['thin', 'thick', 'multi'].indexOf(val) !== -1,
        },
        showLegend: {
            type: Boolean,
            default: false,
        },
        status: {
            type: String,
            default: stat.STANDBY,
        }
    },
    computed: {
        isLoading(){
            return this.status === stat.LOADING;
        },
        cardClass(){
            return statusBuilder('RPBulletGraph', this.status);
        },
        total(){
            return this.chartData.reduce(function(r, b) {
                return r + b.count;
            }, 0);
        },
        chartDataComputed(){
            return this.chartData.map(d => {
                const percent = this.total === 0 ? 0 : Math.round((d.count / this.total) * 100);
                return {
                    ...d,
                    percent,
                    color: has(d, 'color') ? d.color : getRandomColor(JSON.stringify(d)),
                };
            });
        },
        legend(){
            return this.chartDataComputed.map(d => ({
                label: has(d, 'label') ? d.label : '',
                color: d.color,
                percent: d.percent,
            }));
        },
    }
};
</script>
<style lang="scss">
    .RPBulletGraph {
        min-width: 18.75rem;
        border-radius: 4px;
        box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08);
        padding: 1rem;
        background: $white;

        @include loading(100%, 100%);

        &__header-bar {
            display: flex;
            align-items: center;
            margin-bottom: 1rem;
        }
        &__header {
            display: flex;
            align-items: center;
            flex-grow: 1;
        }
        &__header-text {
            color: $text-dark;
            font-weight: 600;
        }
        &__header-tooltip {
            margin-left: 0.25rem;
        }
        &__chart-container {
            display: flex;
            flex-direction: column;
        }
        &__chart {
            margin-bottom: 1rem;
        }
    }
</style>
